import { supabase } from '../../supabaseClient';

/**
 * Wait for a usable access token before a request whose answer depends on WHO is asking.
 *
 * Why (2026-10-13): the app treats the stored session as "signed in" (storedSession.js) so it works offline, but an
 * access token older than an hour has to be refreshed first. In that gap supabase-js sends the publishable key alone,
 * and an RPC that reads auth.uid() answers as nobody: list_my_festival_maps returned [] at 18:03:30, the token came
 * at 18:03:32, and Maps showed "going to nothing" for 20 minutes (its cache time) to someone going to three shows.
 *
 * Resolves with the session once there is one (immediately when the token is fresh; on SIGNED_IN / TOKEN_REFRESHED
 * otherwise). Rejects after `timeoutMs`, so the query errors and keeps what it had instead of caching an empty answer.
 */
export async function requireSession(timeoutMs = 8000) {
  const { data } = await supabase.auth.getSession();
  if (data?.session?.access_token) return data.session;
  return new Promise((resolve, reject) => {
    let settled = false;
    let timer = null;
    let sub = null;
    const done = (fn, v) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // auth-js can call back before onAuthStateChange returns; unsubscribe once we have the handle.
      if (sub) sub.unsubscribe();
      else queueMicrotask(() => sub?.unsubscribe());
      fn(v);
    };
    sub = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token) done(resolve, session);
    }).data.subscription;
    if (settled) {
      sub.unsubscribe();
      return;
    }
    timer = setTimeout(() => {
      const err = new Error('No signed-in session yet');
      err.code = 'NO_SESSION';
      done(reject, err);
    }, timeoutMs);
  });
}
